import {
  computed, nextTick, ref, watch,
} from '@vue/composition-api';

import usePeerConnection from '@/compositions/usePeerConnection';
import useRoom from '@/compositions/useRoom';
import useStreams from '@/compositions/useStreams';
import useSocket from '@/compositions/useSocket';
import useVideo from '@/compositions/useVideo';
import useCamera from '@/compositions/useCamera';
import useUser from '@/compositions/useUser';
import usePublishers from '@/compositions/usePublishers';
import useErrors from '@/compositions/useErrors';
import useJanusSession from '@/compositions/useJanusSession';
import { pushDebugLog } from '@/helpers';

const publisher = ref({});

const janusSession = ref({
  room: null,
  janusRoomId: null,
  janusRoomPrivateMemberId: null,
  janusRoomMemberId: null,
  sessionId: null,
});
const published = ref(false);
const name = ref(window.localStorage.getItem('publisherName'));
const handle = ref(null);

const { connection, onIceCandidate, restartConnection } = usePeerConnection();

export default function useJanusPublisher() {
  const activityInterval = ref(null);

  const { networkError } = useErrors();
  const { session, createSession } = useJanusSession();

  const { registerMessage, sendEvent } = useSocket();

  const {
    publishers, fetch, addPublisher, deletePublisher, clearPublishers,
  } = usePublishers();
  const self = computed(() => publishers.value.find(
    (p) => p.data.janusRoomMemberId === this.janusRoomMemberId,
  ));
  const {
    requestMediaStream, deletePublisherFromLevel, myStream,
    broadcastStream, setMyStream, filterStreamsByPublishers, microphoneDisabled, cameraDisabled,
    stopAllTracks, setCurrentStreamMyStream,
  } = useStreams();

  const {
    room, candidate, auth, processRecordRoom, updateRoom,
  } = useRoom();
  janusSession.value.room = room;
  const { unMute } = useVideo();
  const { loadCameras } = useCamera();
  const { chatUserId } = useUser();

  async function fetchPublishers() {
    pushDebugLog('janus:publisher', 'fetchPublishers started', {
      roomId: room.value,
    });
    const publishersFetched = await fetch(room);
    pushDebugLog('janus:publisher', 'fetchPublishers finished', {
      roomId: room.value,
      count: publishersFetched.length,
    });
    filterStreamsByPublishers(publishersFetched);
  }

  function setupJanusSession() {
    janusSession.value = {
      room: room.value,
      janusRoomId: publisher.value.data.janusRoomId,
      janusRoomPrivateMemberId: publisher.value.data.janusRoomPrivateMemberId,
      janusRoomMemberId: publisher.value.data.janusRoomMemberId,
      sessionId: session.value.data.id,
    };
  }

  async function registerPublisher(offer) {
    pushDebugLog('janus:publisher', 'action:join-room sent', {
      roomId: room.value,
      sessionId: session.value?.data?.id,
      name: name.value,
      audio: !microphoneDisabled.value,
      video: !cameraDisabled.value,
    });
    sendEvent('action:join-room', {
      room: room.value,
      auth: { admin: auth.value, candidate: candidate.value },
      name: name.value,
      audio: !microphoneDisabled.value,
      video: !cameraDisabled.value,
      offer,
      chatUserId: chatUserId.value,
      sessionId: session.value.data.id,
    });
  }

  function stopActivityUpdate() {
    clearInterval(activityInterval.value);
  }

  function sendCandidate(_candidate) {
    sendEvent('publisher:candidate', { candidate: _candidate });
  }

  async function restartStream() {
    pushDebugLog('janus:publisher', 'restartStream started');
    await createSession();
    await connection.value.close();
    await restartConnection();
    await stopAllTracks();
    pushDebugLog('janus:publisher', 'restartStream finished');
  }

  async function broadcastMyStream() {
    pushDebugLog('janus:publisher', 'broadcastMyStream started', {
      cameraDisabled: cameraDisabled.value,
      microphoneDisabled: microphoneDisabled.value,
    });
    const mediaStream = await requestMediaStream();
    if (!mediaStream) {
      pushDebugLog('janus:publisher', 'broadcastMyStream skipped: no media stream');
      return null;
    }

    setMyStream(mediaStream);

    const offer = await broadcastStream(mediaStream, connection.value);

    pushDebugLog('janus:publisher', 'broadcastMyStream offer created', {
      audioTracks: mediaStream.getAudioTracks().length,
      videoTracks: mediaStream.getVideoTracks().length,
    });

    return offer;
  }

  async function startPublish(restart) {
    if ((published.value && !restart)) {
      pushDebugLog('janus:publisher', 'startPublish skipped: already published');
      return;
    }

    pushDebugLog('janus:publisher', 'startPublish started', {
      restart: !!restart,
      roomId: room.value,
    });

    if (restart) {
      await restartStream();
    }

    unMute();
    await loadCameras();
    const offer = await broadcastMyStream();
    await registerPublisher(offer);
    published.value = true;
    pushDebugLog('janus:publisher', 'startPublish finished', {
      roomId: room.value,
      published: published.value,
    });
  }

  function registerPublisherHandlers() {
    watch(networkError, async () => {
      if (networkError.value) {
        setCurrentStreamMyStream();
        clearPublishers();
      } else {
        await startPublish(true);
        await fetchPublishers();
      }
    });

    onIceCandidate((_candidate) => {
      sendCandidate(_candidate || { completed: true });
    });

    registerMessage('publisher:deleted', async (_publisher) => {
      deletePublisher(_publisher);
      deletePublisherFromLevel(_publisher.data.janusRoomMemberId);
    });

    registerMessage('publisher:updated', async () => {
      await fetchPublishers();
    });

    registerMessage('publisher:configured', async ({ jsep, publisher: _publisher }) => {
      publisher.value = _publisher;
      setupJanusSession();
      pushDebugLog('janus:publisher', 'publisher:configured received', {
        janusRoomId: _publisher?.data?.janusRoomId,
        janusRoomMemberId: _publisher?.data?.janusRoomMemberId,
      });
      await connection.value.setRemoteDescription(new RTCSessionDescription(jsep));
    });

    registerMessage('room:joined', async (pub) => {
      pushDebugLog('janus:publisher', 'room:joined received', {
        janusRoomMemberId: pub?.data?.janusRoomMemberId,
      });
      addPublisher(pub);
    });

    activityInterval.value = setInterval(() => {
      if (!janusSession.value.sessionId) {
        return;
      }
      sendEvent('action:last-activity', {
        roomId: janusSession.value.room,
        janusRoomMemberId: janusSession.value.janusRoomMemberId,
        sessionId: janusSession.value.sessionId,
      });
    }, 5000);
  }

  async function processRecord(record = true) {
    try {
      await processRecordRoom(record);
    } catch (e) {
      console.error('VideoRoomPublisherJanusPlugin, cannot set record', e);
    }
  }

  function setName(_name) {
    window.localStorage.setItem('publisherName', _name);
    name.value = _name;
  }

  return {
    publisher,
    processRecord,
    fetchPublishers,
    startPublish,
    janusSession,
    publishers: computed(() => publishers.value),
    filteredPublishers: computed(() => publishers.value.filter(
      (p) => p.data.janusRoomMemberId !== janusSession.value.janusRoomMemberId,
    )),
    self,
    connection,
    published,
    name,
    activityInterval,
    setName,
    stopActivityUpdate,
    registerPublisherHandlers,
  };
}
